import { useEffect, useState, lazy, Suspense, type ComponentType } from "react";

/**
 * DeferredClient — mounts a heavy, non-critical client-only widget after
 * the browser becomes idle (or after a small timeout fallback).
 *
 * Why: keeps the main thread free during initial paint / hydration so
 * LCP, TBT and FID stay healthy. Components are loaded with React.lazy
 * so their JS bundles split out of the main chunk.
 */
export function DeferredClient({
  loader,
  delayMs = 2500,
}: {
  loader: () => Promise<{ default: ComponentType<unknown> } | { [k: string]: ComponentType<unknown> }>;
  delayMs?: number;
}) {
  const [Comp, setComp] = useState<ComponentType<unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      const Lazy = lazy(async () => {
        const mod: any = await loader();
        // Accept either default export or first named export.
        const exported = mod.default ?? Object.values(mod)[0];
        return { default: exported as ComponentType<unknown> };
      });
      setComp(() => Lazy as unknown as ComponentType<unknown>);
    };

    const w = window as any;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(start, { timeout: delayMs });
      return () => { cancelled = true; w.cancelIdleCallback?.(id); };
    }
    const id = window.setTimeout(start, delayMs);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [loader, delayMs]);

  if (!Comp) return null;
  return (
    <Suspense fallback={null}>
      <Comp />
    </Suspense>
  );
}
